import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Shield, Zap, Users } from 'lucide-react';

const AboutSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="relative bg-black py-20 md:py-28 lg:py-36 overflow-hidden">
      {/* Background Crypto Image */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1621761191319-c6fb62004040?q=80&w=2787&auto=format&fit=crop"
          alt=""
          className="w-full h-full object-cover opacity-[0.04]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/98 to-black/95" />
      </div>
      
      {/* Subtle gradient */}
      <div className="absolute top-1/2 left-0 w-[600px] h-[600px] bg-gradient-radial from-amber-900/5 via-transparent to-transparent blur-3xl" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-gradient-radial from-amber-800/5 via-transparent to-transparent blur-3xl" />
      
      <div className="relative z-10 max-w-[1600px] mx-auto px-4 md:px-8 lg:px-16">
        {/* Section Label */}
        <div 
          className="mb-10 md:mb-16"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          <p className="text-white/30 text-[10px] md:text-xs font-medium tracking-[0.2em] md:tracking-[0.3em] mb-4 md:mb-6">
            [ ABOUT US ]
          </p>
        </div>

        {/* Opening Statement - The Vision */}
        <div 
          className="max-w-4xl mb-12 md:mb-20"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1) 100ms'
          }}
        >
          <h2 className="text-xl md:text-2xl lg:text-3xl font-light text-white/90 leading-relaxed mb-6 md:mb-8">
            Every major shift in history has followed the same pattern.
          </h2>
          <p className="text-white/50 text-sm md:text-base font-light leading-relaxed mb-4">
            A new idea emerges. Few believe in it. Fewer take the risk.
            <br className="hidden md:block" />
            And those who do—early—<span className="text-amber-400/80">shape the future.</span>
          </p>
        </div>

        {/* Bitcoin Statement */}
        <div 
          className="mb-12 md:mb-20"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1) 200ms'
          }}
        >
          <div className="bg-gradient-to-r from-amber-500/10 to-transparent border-l-2 border-amber-500/50 pl-4 md:pl-8 py-4 md:py-6 max-w-3xl">
            <p className="text-lg md:text-xl lg:text-2xl font-light text-white/90 leading-relaxed mb-3">
              Bitcoin is doing exactly that.
            </p>
            <p className="text-white/50 text-sm md:text-base font-light leading-relaxed">
              What started as an experiment is now rewriting the foundations of money, finance, and ownership. 
              Those who step into innovation early don't just witness change—<span className="text-white/70">they benefit from it.</span> 
              Risk-takers, builders, and believers have always tasted the rewards first.
            </p>
          </div>
        </div>

        {/* Revolution Statement */}
        <div 
          className="text-center max-w-2xl mx-auto mb-12 md:mb-20"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1) 300ms'
          }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full mb-4 md:mb-6">
            <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-amber-500" />
            <span className="text-amber-500/90 text-[10px] md:text-xs font-medium tracking-wider">THE NEW REVOLUTION</span>
          </div>
          <p className="text-xl md:text-2xl lg:text-3xl font-light text-white leading-relaxed mb-3">
            This is the new revolution of the crypto world.
          </p>
          <p className="text-amber-400 text-lg md:text-xl font-medium">
            And you are still early.
          </p>
        </div>

        {/* Company Story */}
        <div 
          className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 mb-12 md:mb-20"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1) 400ms'
          }}
        >
          {/* Left - Our Story */}
          <div>
            <h3 className="text-lg md:text-xl lg:text-2xl font-light text-white mb-4 md:mb-6">
              Coin Bills was born from this belief.
            </h3>
            <p className="text-white/50 text-sm md:text-base font-light leading-relaxed mb-4 md:mb-6">
              We are a collective of crypto-passionate traders and engineers, driven by years of hands-on experience 
              in digital assets, market cycles, and automation. Our journey has been built through 
              <span className="text-white/70"> real trading, real strategies, and real market exposure</span>—not theory.
            </p>
            <p className="text-white/50 text-sm md:text-base font-light leading-relaxed">
              We combine disciplined trading strategies with secure, API-based automation to help you 
              <span className="text-amber-400/80"> trade smarter, not harder</span>—while keeping full custody of your assets at all times.
            </p>
          </div>

          {/* Right - Key Features */}
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 md:p-6">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-amber-500/10 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <Users className="w-4 h-4 md:w-5 md:h-5 text-amber-500" />
              </div>
              <h4 className="text-white/90 text-sm md:text-base font-medium mb-1 md:mb-2">Experienced Team</h4>
              <p className="text-white/40 text-[10px] md:text-xs font-light leading-relaxed">Crypto-native traders & engineers</p>
            </div>
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 md:p-6">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-amber-500/10 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <Shield className="w-4 h-4 md:w-5 md:h-5 text-amber-500" />
              </div>
              <h4 className="text-white/90 text-sm md:text-base font-medium mb-1 md:mb-2">Non-Custodial</h4>
              <p className="text-white/40 text-[10px] md:text-xs font-light leading-relaxed">Your keys, your coins</p>
            </div>
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 md:p-6">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-amber-500/10 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <Zap className="w-4 h-4 md:w-5 md:h-5 text-amber-500" />
              </div>
              <h4 className="text-white/90 text-sm md:text-base font-medium mb-1 md:mb-2">API Automation</h4>
              <p className="text-white/40 text-[10px] md:text-xs font-light leading-relaxed">Secure, trade-only access</p>
            </div>
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 md:p-6">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-green-500/10 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h4 className="text-white/90 text-sm md:text-base font-medium mb-1 md:mb-2">Real Results</h4>
              <p className="text-white/40 text-[10px] md:text-xs font-light leading-relaxed">Battle-tested strategies</p>
            </div>
          </div>
        </div>

        {/* Mission Statement */}
        <div 
          className="bg-gradient-to-br from-white/[0.03] to-transparent border border-white/[0.06] rounded-2xl p-6 md:p-10 lg:p-12 mb-12 md:mb-16"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1) 500ms'
          }}
        >
          <p className="text-white/30 text-[10px] md:text-xs font-medium tracking-[0.2em] mb-4 md:mb-6">
            [ OUR MISSION ]
          </p>
          <p className="text-white/70 text-base md:text-lg lg:text-xl font-light leading-relaxed max-w-3xl">
            To empower you with professional-grade automation, transparent strategies, and the confidence 
            to participate in the future of digital finance.
          </p>
        </div>

        {/* Closing Statement */}
        <div 
          className="text-center"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1) 600ms'
          }}
        >
          <div className="max-w-2xl mx-auto">
            <p className="text-white/50 text-sm md:text-base font-light mb-2">
              You focus on growth.
            </p>
            <p className="text-amber-400/80 text-sm md:text-base font-medium mb-6 md:mb-8">
              We take care of the execution.
            </p>
            <div className="h-px w-16 bg-gradient-to-r from-transparent via-amber-500/30 to-transparent mx-auto mb-6 md:mb-8" />
            <p className="text-white/40 text-xs md:text-sm font-light mb-1">
              Welcome to the future of digital asset management.
            </p>
            <p className="text-white/90 text-base md:text-lg font-light">
              Welcome to <span className="brand-font text-amber-400">COIN BILLS</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
